/**
 * Form Components
 *
 * Handles form validation, submission, and interactions.
 */

/**
 * Initialize form functionality
 */
const formControllers = new WeakMap<HTMLFormElement, AbortController>();

export function initForms(): void {
  const forms = document.querySelectorAll('form[data-wm-form]');

  forms.forEach(form => {
    form.setAttribute('aria-live', 'polite');
    form.addEventListener('submit', handleFormSubmit);
  });

  // Add real-time validation
  const inputs = document.querySelectorAll('input[required], textarea[required]');
  inputs.forEach(input => {
    input.addEventListener('blur', e => validateField(e as FocusEvent));
    input.addEventListener('input', e => clearFieldError(e));
  });
}

/**
 * Handle form submission
 */
function handleFormSubmit(e: Event): void {
  e.preventDefault();
  if (!(e.target instanceof HTMLFormElement)) return;
  const form = e.target;

  if (validateForm(form)) {
    // Form is valid, proceed with submission
    submitForm(form);
  }
}

/**
 * Validate entire form
 */
function validateForm(form: HTMLFormElement): boolean {
  const inputs = form.querySelectorAll('input[required], textarea[required]');
  let isValid = true;

  inputs.forEach(input => {
    const fakeEvent = new FocusEvent('blur', { bubbles: false, cancelable: true });
    Object.defineProperty(fakeEvent, 'target', { value: input, enumerable: true });
    if (!validateField(fakeEvent)) {
      isValid = false;
    }
  });

  return isValid;
}

/**
 * Validate individual field
 */
function validateField(e: FocusEvent): boolean {
  const field = e.target as HTMLInputElement | HTMLTextAreaElement;
  const isValid = field.checkValidity();

  if (!isValid) {
    showFieldError(field);
  } else {
    const fakeEvent = new Event('input', { bubbles: true, cancelable: true });
    Object.defineProperty(fakeEvent, 'target', { value: field, enumerable: true });
    clearFieldError(fakeEvent);
  }

  return isValid;
}

/**
 * Show field error
 */
function showFieldError(field: HTMLInputElement | HTMLTextAreaElement): void {
  field.classList.add('has-error');

  let errorMessage = field.nextElementSibling;
  if (!errorMessage || !errorMessage.classList.contains('field-error')) {
    errorMessage = document.createElement('span');
    errorMessage.className = 'field-error';
    field.parentNode?.insertBefore(errorMessage, field.nextSibling);
  }

  errorMessage.textContent = field.validationMessage || 'This field is required';
}

/**
 * Clear field error
 */
function clearFieldError(e: Event): void {
  const field = e.target as HTMLInputElement | HTMLTextAreaElement;
  field.classList.remove('has-error');

  const errorMessage = field.nextElementSibling;
  if (errorMessage && errorMessage.classList.contains('field-error')) {
    errorMessage.remove();
  }
}

/**
 * Submit form via AJAX
 */
function submitForm(form: HTMLFormElement): void {
  if (typeof window.wmTheme === 'undefined' || !window.wmTheme?.nonce) {
    showFormError(form, 'Security token missing. Please refresh and try again.');
    return;
  }

  // Cancel any in-flight request for this form
  formControllers.get(form)?.abort();
  const controller = new AbortController();
  formControllers.set(form, controller);

  const formData = new FormData(form);
  formData.append('_wpnonce', window.wmTheme.nonce);

  const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;

  if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = 'Submitting...';
  }

  if (window.wmTheme?.ajaxUrl) {
    fetch(window.wmTheme.ajaxUrl, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          showFormSuccess(form);
        } else {
          showFormError(form, data.data?.message || 'An error occurred');
        }
      })
      .catch(error => {
        if ((error as Error).name === 'AbortError') return;
        showFormError(form, 'Network error. Please try again.');
        console.error('Form submission error:', error);
      })
      .finally(() => {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = submitButton.dataset.originalText || 'Submit';
        }
      });
  } else {
    // Fallback to standard form submission
    form.submit();
  }
}

/**
 * Show form success message
 */
function showFormSuccess(form: HTMLFormElement): void {
  const successMessage = document.createElement('div');
  successMessage.className = 'form-success';
  successMessage.textContent = 'Thank you! Your message has been sent.';
  form.insertBefore(successMessage, form.firstChild);

  setTimeout(() => {
    successMessage.remove();
    form.reset();
  }, 5000);
}

/**
 * Show form error message
 */
function showFormError(form: HTMLFormElement, message: string): void {
  const errorMessage = document.createElement('div');
  errorMessage.className = 'form-error';
  errorMessage.textContent = message;
  form.insertBefore(errorMessage, form.firstChild);

  setTimeout(() => {
    errorMessage.remove();
  }, 5000);
}
